/**
 * Componente de Alerta para Licencias Bajas
 * CARDS CON DIFERENTES ALERTAS SEGUN NIVEL DE URGENCIA PARA COMPRAR LICENCIAS
 * Muestra una advertencia cuando quedan pocas licencias disponibles (≤ 5)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Button from '@mui/material/Button';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

const LicenseAlertCard = ({ 
  availableLicenses, 
  threshold = 5,
  onBuyClick,
  showBuyButton = true 
}) => {
  const navigate = useNavigate();

  // No mostrar si hay suficientes licencias
  if (availableLicenses > threshold) {
    return null;
  }

  // Determinar nivel de urgencia
  const getAlertLevel = () => {
    if (availableLicenses === 0) return 'critical';
    if (availableLicenses <= 2) return 'high';
    return 'medium';
  };

  const alertLevel = getAlertLevel();

  // Configuración de colores y mensajes según el nivel
  const alertConfig = {
    critical: {
      bgColor: 'error',
      icon: 'error',
      title: '¡Sin licencias disponibles!',
      message: 'No tienes licencias disponibles. Adquiere más licencias para continuar evaluando candidatos.',
      buttonText: 'Comprar ahora',
      buttonColor: 'error',
    },
    high: {
      bgColor: 'warning',
      icon: 'warning',
      title: '¡Atención! Quedan muy pocas licencias',
      message: `Solo tienes ${availableLicenses} licencia${availableLicenses === 1 ? '' : 's'} disponible${availableLicenses === 1 ? '' : 's'}. Te recomendamos adquirir más para evitar interrupciones.`,
      buttonText: 'Comprar licencias',
      buttonColor: 'warning',
    },
    medium: {
      bgColor: 'warning',
      icon: 'info',
      title: 'Licencias por agotarse',
      message: `Tienes ${availableLicenses} licencias disponibles. Considera renovar pronto para asegurar la continuidad de tus evaluaciones.`,
      buttonText: 'Ver planes',
      buttonColor: 'warning',
    },
  };

  const config = alertConfig[alertLevel];

  const handleBuyClick = () => {
    if (onBuyClick) {
      onBuyClick();
    } else {
      // Redirigir a la página de compra de licencias
      navigate('/gestion-licencias/comprar');
    }
  };

  return (
    <Card
      sx={{
        background: (theme) => {
          const colors = {
            critical: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
            high: 'linear-gradient(135deg, #ff2626 0%, #ff4d4d 100%)',
            medium: 'linear-gradient(135deg, #ff2626 0%, #ff4d4d 100%)',
          };
          return colors[alertLevel];
        },
        color: 'white',
        boxShadow: 3,
      }}
    >
      <MDBox p={3}>
        <MDBox display="flex" alignItems="center" mb={2}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="3rem"
            height="3rem"
            borderRadius="md"
            bgColor="white"
            color={config.bgColor}
            mr={2}
            sx={{ boxShadow: 2 }}
          >
            <Icon fontSize="large">{config.icon}</Icon>
          </MDBox>
          <MDBox>
            <MDTypography variant="h5" fontWeight="bold" color="white">
              {config.title}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant="body2" color="white" opacity={0.95}>
            {config.message}
          </MDTypography>
        </MDBox>

        {/* Indicador visual de licencias */}
        <MDBox mb={3} p={2} borderRadius="lg" bgColor="rgba(255, 255, 255, 0.2)">
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" color="white" fontWeight="medium">
              Licencias disponibles:
            </MDTypography>
            <MDTypography variant="h3" color="white" fontWeight="bold">
              {availableLicenses}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Botones de acción */}
        {showBuyButton && (
          <MDBox display="flex" gap={2}>
            <MDButton
              variant="contained"
              color="white"
              size="large"
              fullWidth
              onClick={handleBuyClick}
              sx={{
                fontWeight: 'bold',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: 4,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Icon sx={{ mr: 1 }}>shopping_cart</Icon>
              {config.buttonText}
            </MDButton>
          </MDBox>
        )}

        {/* Información adicional */}
        {alertLevel === 'critical' && (
          <MDBox mt={2} textAlign="center">
            <MDTypography variant="caption" color="white" opacity={0.9}>
              Contacta a soporte si necesitas asistencia inmediata
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
};

LicenseAlertCard.propTypes = {
  availableLicenses: PropTypes.number.isRequired,
  threshold: PropTypes.number,
  onBuyClick: PropTypes.func,
  showBuyButton: PropTypes.bool,
};

export default LicenseAlertCard;
